import React, { useState, useEffect } from 'react'
import { Download, Upload, Link, Calendar, RefreshCw, Check, X, AlertCircle, Plus, Eye } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface CalendarSource {
  id: string
  name: string
  url: string
  type: 'ical' | 'google' | 'outlook' | 'cobb_county'
  is_active: boolean
  last_sync?: string
  event_count?: number
  error?: string
}

interface CalendarEvent {
  title: string
  date: string
  type: string
  description?: string
}

export const SchoolCalendarImport: React.FC = () => {
  const [sources, setSources] = useState<CalendarSource[]>([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [previewEvents, setPreviewEvents] = useState<CalendarEvent[]>([])
  const [showPreview, setShowPreview] = useState(false)
  const [newSource, setNewSource] = useState({
    name: '',
    url: '',
    type: 'ical' as const
  })

  useEffect(() => {
    loadSources()
  }, [])

  const loadSources = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/external-calendars')
      setSources(data || [])
    } catch (error) {
      console.error('Failed to load calendar sources:', error)
      setSources([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddSource = async () => {
    if (!newSource.name || !newSource.url) {
      toast.error('Please provide both name and URL')
      return
    }

    try {
      await apiClient.post('/api/external-calendars', newSource)
      toast.success('Calendar source added successfully')
      setShowAddModal(false)
      setNewSource({ name: '', url: '', type: 'ical' })
      loadSources()
    } catch (error) {
      toast.error('Failed to add calendar source')
    }
  }

  const handleSync = async (sourceId: string) => {
    setSyncing(sourceId)
    try {
      await apiClient.post(`/api/external-calendars/${sourceId}/sync`)
      toast.success('Calendar synced successfully')
      loadSources()
    } catch (error) {
      toast.error('Failed to sync calendar')
    } finally {
      setSyncing(null)
    }
  }

  const handlePreview = async (sourceId: string) => {
    try {
      const data = await apiClient.get(`/api/external-calendars/${sourceId}/preview`)
      setPreviewEvents(data.events || [])
      setShowPreview(true)
    } catch (error) {
      toast.error('Failed to preview calendar')
    }
  }

  const handleToggleActive = async (sourceId: string, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/external-calendars/${sourceId}`, { is_active: !isActive })
      loadSources()
    } catch (error) {
      toast.error('Failed to update calendar status')
    }
  }

  const handleDelete = async (sourceId: string) => {
    if (!confirm('Are you sure you want to delete this calendar source?')) return

    try {
      await apiClient.delete(`/api/external-calendars/${sourceId}`)
      toast.success('Calendar source deleted')
      loadSources()
    } catch (error) {
      toast.error('Failed to delete calendar source')
    }
  }

  const predefinedSources = [
    { name: 'Cobb County Schools', url: 'https://www.cobbk12.org/calendar', type: 'cobb_county' },
    { name: 'US Federal Holidays', url: 'https://www.officeholidays.com/ics/usa', type: 'ical' },
    { name: 'Catholic Liturgical Calendar', url: 'https://www.catholic.org/calendar/', type: 'ical' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">School Calendar Import</h2>
          <p className="text-gray-600">Import and sync school calendars and holidays</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Calendar
        </button>
      </div>

      {/* Quick Add Predefined Sources */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-3">Quick Add Common Calendars</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {predefinedSources.map(source => (
            <button
              key={source.name}
              onClick={() => {
                setNewSource(source)
                setShowAddModal(true)
              }}
              className="p-3 bg-white rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="font-medium">{source.name}</div>
              <div className="text-sm text-gray-500">Click to add</div>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Sources List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Connected Calendars</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : sources.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No calendar sources connected</p>
            <p className="text-sm mt-1">Add a calendar source to start syncing events</p>
          </div>
        ) : (
          <div className="divide-y">
            {sources.map(source => (
              <div key={source.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="font-medium text-gray-900">{source.name}</h4>
                      {source.is_active ? (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                      ) : (
                        <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      <Link className="w-3 h-3 inline mr-1" />
                      {source.url}
                    </div>
                    {source.last_sync && (
                      <div className="text-sm text-gray-500 mt-1">
                        Last synced: {format(new Date(source.last_sync), 'MMM d, yyyy h:mm a')}
                        {source.event_count && ` • ${source.event_count} events`}
                      </div>
                    )}
                    {source.error && (
                      <div className="flex items-center text-sm text-red-600 mt-1">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {source.error}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handlePreview(source.id)}
                      className="p-2 text-gray-600 hover:text-gray-900"
                      title="Preview Events"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSync(source.id)}
                      disabled={syncing === source.id}
                      className="p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      title="Sync Now"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncing === source.id ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleToggleActive(source.id, source.is_active)}
                      className={`p-2 ${source.is_active ? 'text-green-600' : 'text-gray-400'}`}
                      title={source.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {source.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(source.id)}
                      className="p-2 text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Calendar Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Add Calendar Source</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Calendar Name</label>
                <input
                  type="text"
                  value={newSource.name}
                  onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., School District Calendar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Calendar URL</label>
                <input
                  type="url"
                  value={newSource.url}
                  onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://example.com/calendar.ics"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Calendar Type</label>
                <select
                  value={newSource.type}
                  onChange={(e) => setNewSource({ ...newSource, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="ical">iCal (.ics)</option>
                  <option value="google">Google Calendar</option>
                  <option value="outlook">Outlook Calendar</option>
                  <option value="cobb_county">Cobb County Schools</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewSource({ name: '', url: '', type: 'ical' })
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSource}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Calendar Event Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {previewEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No upcoming events found</p>
            ) : (
              <div className="space-y-2">
                {previewEvents.slice(0, 20).map((event, idx) => (
                  <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50">
                    <div className="font-medium">{event.title}</div>
                    <div className="text-sm text-gray-500">
                      {format(new Date(event.date + 'T12:00:00'), 'MMM d, yyyy')}
                      {event.type && ` • ${event.type}`}
                    </div>
                    {event.description && (
                      <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                    )}
                  </div>
                ))}
                {previewEvents.length > 20 && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    And {previewEvents.length - 20} more events...
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}